// TermMngEdit.js
import React, { useEffect, useState } from 'react';
import { Box } from '@mui/system';
import { styled } from '@mui/material/styles';
import { TextField, Button, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress } from '@mui/material';
import { AppPalette, CvoCodes, AppWord } from '../../model/AppConst';
import TextCombo from '../common/TextCombo';
import { TermMngDictionary } from './TermMngGql';
import ConfirmDialog from '../message/ConfirmDialog';
import ValueUtil from '../../model/ValueUtil';
import { useReactiveVar } from '@apollo/client';
import { codeTreeRepo, reflectInput } from '../../model/CvoModel';
import CodeSelector from '../common/CodeSelector';
import { ActionsButtons, ActionsMessage, ActionsWrapper } from '../common/CvoStyled';

const ColumnMargin = 5;

const EditorTitle = styled(DialogTitle)({ backgroundColor: AppPalette.HeaderBackground, color: 'white', minWidth: 300, });
const EditorContentRow = styled(Box)({ padding: 0, });
const InputColumn = styled(Box)({
    display: 'inline-block', // width: ColumnBoxWidth, // adjust here.
    paddingTop: 10,
    margin: ColumnMargin,
    verticalAlign: 'top'
});

const FieldBox = styled(Box)({ display: 'block', paddingTop: 10, });

const ViewBox = styled(Box)({display:'flex', width: 200, marginTop:10});
const ViewLabel = styled(Box)({minWidth:90, color:'#35f', fontSize:'0.85rem'});
const ViewValue = styled(Box)({flexGrow:1});


const InputMap = TermMngDictionary.inputMap; // 입력양식 상수, validation
const defaultInputData = ValueUtil.defaultsFromInputMap(InputMap); // 입력양식 상수 중 기초 데이터(default)만 추출.

// 신규입력과 편집입력의 차이 및 방식 차이 때문에 각각 별개로 만든다.
export default function TermMngEdit({
    open, // 편집 후 저장이 성공적이면 open==false 된다.
    item, // isEdit, // == Boolean(item)
    customerList, // 회계용 고객목록.
    onClose, // Cancel editing.
    onClickMutate, // Ask controller to submit.
    responseSaving // Response if result is NOT OK.
}) {
    const CodeTree = useReactiveVar(codeTreeRepo);
    const [NetworkList, setNetworkList] = useState([]);
    const [TermStateList, setTermStateList] = useState([]);
    const [OpenForList, setOpenForList] = useState([]);
    const [FirmTypeList, setFirmTypeList] = useState([]);
    const [ModelList, setModelList] = useState([]);
    const [BillTypeList, setBillTypeList] = useState([]);
    const [ContrMonthList, setContrMonthList] = useState([]);
    const [ContrAmountList, setContrAmountList] = useState([]);
    const [TermChangeList, setTermChangeList] = useState([]);
    const [changed, setChanged] = useState(false); // 입력으로 인한 변경이 있는가?
    const [oldData, setOldData] = useState({});
    const [inputData, setInputData] = useState(defaultInputData); // 입력한 데이터.
	const [inputError, setInputError] = useState({}); // 입력된 것들 중 오류 여부 표시.
    const [hasError, setHasError] = useState(false); // 하나라도 오류가 있는가? 입력되지 않은 것이 있는가?
    const [errorLabels, setErrorLabels] = useState([]); // 오류가 있는 필드의 라벨들.
    const [promptToConfirm, setPromptToConfirm] = useState(null);
    //const [responseAlert, setResponseAlert] = useState(null); // for response error
    const {loading:loadingSave, error:errorSave, data:resultSave} = responseSaving; // 전송 결과에 대한 View 기능 소화.

    useEffect(()=>{
        if(CodeTree) {
            setNetworkList(ValueUtil.codeToSelectList(CvoCodes.AC_NETWORK_CD, CodeTree));
            setTermStateList(ValueUtil.codeToSelectList(CvoCodes.AC_TERM_STATE_CD, CodeTree));
            setOpenForList(ValueUtil.codeToSelectList(CvoCodes.AC_OPEN_FOR_CD, CodeTree));
            setFirmTypeList(ValueUtil.codeToSelectList(CvoCodes.AC_FIRM_TYPE_CD, CodeTree));
            setModelList(ValueUtil.codeToSelectList(CvoCodes.AC_MODEL_CD, CodeTree));
            setBillTypeList(ValueUtil.codeToSelectList(CvoCodes.AC_BILL_TYPE_CD, CodeTree));
            setContrMonthList(ValueUtil.codeToSelectList(CvoCodes.AC_CONTR_MONTH_CD, CodeTree));
            setContrAmountList(ValueUtil.codeToSelectList(CvoCodes.AC_CONTR_AMOUNT_CD, CodeTree));
            setTermChangeList(ValueUtil.codeToSelectList(CvoCodes.AC_TERM_CHANGE_CD, CodeTree));
        }
    }, [CodeTree]);

    useEffect(()=>{
        if(open) {
            item.acTermChangeCd = 'CH_ETC';
            setInputData({...item, changeDate: ValueUtil.getYmdText()}); // 변경일자 기본값은 당일.
            setOldData({...item});
            setInputError({});
            setChanged(false);
            setHasError(false);
            setErrorLabels([]);
        }
    }, [open, item]);

    const changeInputData = (data) => {
		setInputData(data ? data : defaultInputData);
        setChanged(true);
    };

	const resetData = (data) => {
		setInputData(data ? data : defaultInputData);
		setInputError({});
		setChanged(false);
		setHasError(false);
        setErrorLabels([]);
	};

	// 저장 호출은 컨트롤러로 보내고, 그 결과를 responseSaving 받아서 보여준다.
    const onClickSubmit = () => {
        const param = ValueUtil.getDataToSubmit(inputData, InputMap, true); // {...inputData}; true for isEdit
        // add extra data if necessary.

        onClickMutate(param, true); // true for isEdit
    };

    const onClickCancel = () => {
        if(changed) {
            setPromptToConfirm({
                data: true,
                title: '개통정보가 변경되었습니다.',
                messages: ['이 정보를 변경했습니다.', '변경사항을 무시하고 편집을 종료하시겠습니까?'],
                labelToYes: '무시하고 종료',
                callback: (yes) => {
                    setPromptToConfirm(null);
                    if(yes) {
                        resetData();
                        onClose();
                    }
                }
            });
        }
        else {
            resetData();
            onClose();
        }
    };
        
    const onChangeAnyData = (id, value) => {
        const [newData, newError, hasError] = reflectInput(id, value, InputMap, inputData, inputError, true);
        changeInputData(newData);
        setInputError(newError);
        setHasError(hasError);
        setErrorLabels(newError.__errorLabels || []);
    };

    const onChangeTextData = (event) => {
        onChangeAnyData(event.target.id, event.target.value);
        /* // evaluate input data against readiness.
        const [newData, newError, hasError] = ValueUtil.evalTextInput2(event, InputMap, inputData, inputError, true);

        changeInputData(newData);
        setInputError(newError);
        setHasError(hasError); */
    };

    const onChangeNumeric = (event) => {
        if(event.target.value) {
            if(/[^0-9]/.test(event.target.value)) return;
        }
        onChangeTextData(event);
    };

    // 개통상태 코드가 해지인가? 코드값 대신 라벨로 판단(목록 행 표시와 동일 기준).
    const isAbandonState = (cd) => {
        const found = TermStateList.find(v => v.value === cd);
        return Boolean(found && (found.label || '').includes('해지'));
    };

    const onChangeCode = (id, value) => {
        if(id === InputMap.acTermStateCd.id && isAbandonState(value)) {
            // 해지 선택 시 해지일자/변경일자는 당일, 법인#/생년월일, 사업자등록번호는 비운다.
            const today = ValueUtil.getYmdText();
            const data = {...inputData, abandonDate: today, changeDate: today, custCorpNo: '', compBizno: ''};
            const er = {...inputError};
            delete er.abandonDate; delete er.changeDate; delete er.custCorpNo; delete er.compBizno;
            const [newData, newError, hasError] = reflectInput(id, value, InputMap, data, er, true);
            changeInputData(newData);
            setInputError(newError);
            setHasError(hasError);
            setErrorLabels(newError.__errorLabels || []);
            return;
        }
        onChangeAnyData(id, value);
        /* const data = {...inputData};
        data[id] = value;
        changeInputData(data); */
    };

    // redner -------------------
    const renderPromptIgnoreChange = () => {
        const open = Boolean(promptToConfirm);
        const prompt = open ? promptToConfirm : {}; // onClose 오류나지 않도록
        return (
            <ConfirmDialog
                open={open}
                prompt={prompt}
                onClose={prompt.callback}
            />
        );
    };

    const renderTextFieldBox = (dict, value, error, runOnChange, disabled) => {
        return(
            <FieldBox>
                <TextField id={dict.id} size={AppWord.SMALL}
                    type={dict.type}
                    label={dict.label}
                    required={dict.required}
                    value={value}
                    error={error}
                    helperText={dict.help}
                    disabled={disabled}
                    sx={disabled ? { backgroundColor: '#e0e0e0' } : undefined}
                    onChange={runOnChange} />
            </FieldBox>

        );
    };

    const renderDateBox = (dict, value, error, runOnChange) => {
        return(
            <FieldBox>
                <TextField id={dict.id} size={AppWord.SMALL}
                    type={dict.type}
                    label={dict.label}
                    required={dict.required}
                    value={value}
                    error={error}
                    helperText={dict.help}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ max: '9999-12-31' }} // 연도 4자리 제한 - 없으면 연도칸에 6자리까지 입력돼 월/일로 커서가 안 넘어감.
                    onChange={runOnChange} />
            </FieldBox>

        );
    };

    const renderSelectorField = (dict, value, selectFrom) => {
        return(
            <FieldBox>
                <CodeSelector
                    id={dict.id}
                    value={value || dict.default || ""}
                    label={dict.label}
                    codes={selectFrom}
                    onChange={onChangeCode}
                />
            </FieldBox>
        );
    };

    const abandoned = isAbandonState(inputData.acTermStateCd);

    return (
        <Dialog open={open} maxWidth="xl" fullWidth>
            <EditorTitle>개통정보 수정</EditorTitle>
            <DialogContent>
                <EditorContentRow>
                    <InputColumn>
                        {renderTextFieldBox(InputMap.termId, inputData.termId,
                            inputError.termId, onChangeNumeric) /* 단말기ID */}
                        {renderSelectorField(InputMap.acTermChangeCd, inputData.acTermChangeCd, TermChangeList) /* 변경사유 */}
                        {renderDateBox(InputMap.changeDate, inputData.changeDate,
                            inputError.changeDate, onChangeTextData) /* 변경일자 */}
                        <FieldBox>
                            <TextField id={InputMap.changeMemo.id}
                                multiline
                                rows={3}
                                type={InputMap.changeMemo.type}
                                label={InputMap.changeMemo.label}
                                required={InputMap.changeMemo.required}
                                value={inputData.changeMemo}
                                error={inputError.changeMemo}
                                helperText={InputMap.changeMemo.help}
                                onChange={onChangeTextData} />{/* 변경메모 */}
                        </FieldBox>
                        {renderTextFieldBox(InputMap.generalMemo, inputData.generalMemo,
                            inputError.generalMemo, onChangeTextData) /* 적요 */}
                    </InputColumn>
                    <InputColumn>
                        {renderTextFieldBox(InputMap.serialNo, inputData.serialNo,
                            inputError.serialNo, onChangeTextData) /* S/N */}
                        {renderTextFieldBox(InputMap.usimNum, inputData.usimNum,
                            inputError.usimNum, onChangeTextData) /* USIM */}
                        <FieldBox>
                            <TextCombo
                                id={InputMap.custName.id}
                                label={InputMap.custName.label}
                                selectFrom={customerList}
                                uniqueKey={InputMap.custName.id}
                                text={inputData.custName || ''}
                                error={inputError.custName}
                                onChange={(id,value)=>{onChangeTextData({target:{id:id, value:value}})}}
                            />{ /* 업체명 - combo */}
                        </FieldBox>
                        {renderDateBox(InputMap.openDate, inputData.openDate,
                            inputError.openDate, onChangeTextData) /* 개통일자 */}
                        {renderDateBox(InputMap.abandonDate, inputData.abandonDate,
                            inputError.abandonDate, onChangeTextData) /* 해지일자 */}
                        {renderSelectorField(InputMap.acNetworkCd, inputData.acNetworkCd, NetworkList) /* 통신사 */}
                            
                        <ViewBox>
                            <ViewLabel>이전 S/N</ViewLabel>
                            <ViewValue>{oldData.serialNo}</ViewValue>
                        </ViewBox>
                        <ViewBox>
                            <ViewLabel>이전 USIM</ViewLabel>
                            <ViewValue>{oldData.usimNum}</ViewValue>
                        </ViewBox>
                    </InputColumn>
                    <InputColumn>
                        {renderSelectorField(InputMap.acTermStateCd, inputData.acTermStateCd, TermStateList) /* 개통상태 */}
                        {renderSelectorField(InputMap.acOpenForCd, inputData.acOpenForCd, OpenForList) /* 개통구분 */}

                        {renderSelectorField(InputMap.acModelCd, inputData.acModelCd, ModelList) /* 모델 */}
                        {renderTextFieldBox(InputMap.salePrice, inputData.salePrice,
                            inputError.salePrice, onChangeTextData) /* 출고가. text */}
                        {renderTextFieldBox(InputMap.supportAmount, inputData.supportAmount,
                            inputError.supportAmount, onChangeTextData) /* 단말지원약정 */}
                        {renderSelectorField(InputMap.acBillTypeCd, inputData.acBillTypeCd, BillTypeList) /* 요금제 */}
                        {
                            Boolean(oldData.acBillTypeValue) ?
                            <ViewBox>
                                <ViewLabel>이전 모델</ViewLabel>
                                <ViewValue>{oldData.acModelValue}</ViewValue>
                            </ViewBox>
                            : null
                        }
                        {
                            Boolean(oldData.acBillTypeValue) ?
                            <ViewBox>
                                <ViewLabel>이전 요금제</ViewLabel>
                                <ViewValue>{oldData.acBillTypeValue}</ViewValue>
                            </ViewBox>
                            : null
                        }
                    </InputColumn>
                    <InputColumn>
                        {renderSelectorField(InputMap.acContrMonthCd, inputData.acContrMonthCd, ContrMonthList) /* 약정개월 */}
                        {renderSelectorField(InputMap.acContrAmountCd, inputData.acContrAmountCd, ContrAmountList) /* 약정금액 */}

                        {renderSelectorField(InputMap.acFirmTypeCd, inputData.acFirmTypeCd, FirmTypeList) /* 법인구분 */}
                        {renderTextFieldBox(InputMap.custCorpNo, inputData.custCorpNo,
                            inputError.custCorpNo, onChangeNumeric, abandoned) /* 법인#/생년월일 - 해지면 비활성 */}
                        {renderTextFieldBox(InputMap.compBizno, inputData.compBizno,
                            inputError.compBizno, onChangeNumeric, abandoned) /* 사업자등록번호 - 해지면 비활성 */}
                        {renderTextFieldBox(InputMap.openerName, inputData.openerName,
                            inputError.openerName, onChangeTextData) /* 명의자명 */}
                        {
                            Boolean(oldData.openerName) ?
                            <ViewBox>
                                <ViewLabel>이전 명의자</ViewLabel>
                                <ViewValue>{oldData.openerName}</ViewValue>
                            </ViewBox>
                            : null
                        }
                    </InputColumn>
                    <InputColumn>
                        {renderTextFieldBox(InputMap.representitive, inputData.representitive,
                            inputError.representitive, onChangeTextData) /* 대리인 */}
                        {renderTextFieldBox(InputMap.groupMemo, inputData.groupMemo,
                            inputError.groupMemo, onChangeTextData) /* 소그룹 */}
                        {renderTextFieldBox(InputMap.salesStaffName, inputData.salesStaffName,
                            inputError.salesStaffName, onChangeTextData) /* 영업담당자 */}
                        {renderTextFieldBox(InputMap.thermComp, inputData.thermComp,
                            inputError.thermComp, onChangeTextData) /* 온도업체 */}
                        {renderTextFieldBox(InputMap.memoInstall, inputData.memoInstall,
                            inputError.memoInstall, onChangeTextData) /* 설치여부 */}

                    </InputColumn>
                </EditorContentRow>
            </DialogContent>
            <DialogActions>
                <ActionsWrapper>
                    <ActionsMessage labels={errorLabels}/>
                    <ActionsButtons>
                        {
                            loadingSave
                            ?
                            <span>
                                <CircularProgress />
                                &nbsp;데이터 저장 중...
                            </span>
                            :
                            <>
                                <Button
                                    onClick={onClickSubmit}
                                    disabled={hasError || !changed}
                                    variant='contained' color='primary'>
                                    저장</Button>
                                <Button onClick={onClickCancel} color='warning'
                                    variant='contained'>취소</Button>
                            </>
                        }
                    </ActionsButtons>
                </ActionsWrapper>
            </DialogActions>
            {renderPromptIgnoreChange()}
        </Dialog>
    );
}
